import {
  replaceCompIdPlaceholder,
  replaceContentIds,
} from '@wix/editor-elements-common-utils';
import { isFallbackSvg } from '@wix/editor-elements-corvid-utils';
import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import * as vectorImage from '@wix/thunderbolt-commons/dist/vectorImage';
import { isSvgTypeUgc } from '../constants';
import type {
  SvgImageProps,
  SvgImageDefinition,
  SvgImageCarmiProps,
  SvgInfo,
} from '../SvgImage.types';

function getAttributeByName(
  rawSvg: string,
  attributeName: keyof React.SVGAttributes<SVGSVGElement>,
): string | undefined {
  // Match only the first <svg> element, ignoring any nested <svg> tags
  const svgMatch = rawSvg.match(/<svg[^>]*>/);
  if (!svgMatch) {
    return undefined;
  }

  // Extract the attributes from the matched <svg> element
  const attributeMatch = svgMatch[0].match(
    new RegExp(`${attributeName}\\s*=\\s*["']?([^"']*)["']?`),
  );
  return attributeMatch ? attributeMatch[1] : undefined;
}

export function getSvgViewBox(
  rawSvg: string,
  { viewBox, bbox, svgType }: SvgInfo,
) {
  const viewBoxAttr = getAttributeByName(rawSvg, 'viewBox');

  if (!viewBoxAttr && viewBox) {
    return viewBox;
  }
  if (!isSvgTypeUgc(svgType) && bbox) {
    return bbox;
  }

  return viewBoxAttr;
}

export function getWidth(rawSvg: string): string | undefined {
  return getAttributeByName(rawSvg, 'width');
}

export function getHeight(rawSvg: string): string | undefined {
  return getAttributeByName(rawSvg, 'height');
}

function extractSvgInnerContent(svgString: string): string {
  if (!svgString || typeof svgString !== 'string') {
    return '';
  }
  return svgString
    .trim()
    .replace(/^<\?xml[^>]*\?>\s*/i, '') // Remove XML prolog
    .replace(/^<svg[^>]*>/, '') // Remove opening <svg> tag
    .replace(/<\/svg>\s*$/, '') // Remove closing </svg> tag
    .trim();
}

function transformSvgString(svgString: string, id: string) {
  if (isFallbackSvg(svgString)) {
    return svgString;
  }
  svgString = replaceContentIds(svgString, id);
  svgString = replaceCompIdPlaceholder(svgString, id);
  svgString = extractSvgInnerContent(svgString);

  return svgString;
}

export function processSvgProps(
  props: Pick<SvgImageProps, 'svgString' | 'id'>,
  svgInfo: SvgInfo,
) {
  const { svgString = '', id } = props;

  return {
    svgType: svgInfo.svgType,
    viewBox: getSvgViewBox(svgString, svgInfo),
    svgWidth: getWidth(svgString),
    svgHeight: getHeight(svgString),
    bBox: svgInfo.bbox,
    svgString: transformSvgString(svgString, id),
  };
}

export const props = withCompInfo<
  SvgImageProps,
  SvgImageDefinition,
  SvgImageCarmiProps
>()(
  ['compId', 'compData'],
  ({ compData: { svgId, alt, displayMode }, compId }, carmiData) => {
    const { svgString, svgInfo, link } = carmiData;

    return {
      ...processSvgProps({ svgString, id: compId }, svgInfo),
      id: compId,
      svgId,
      link,
      alt,
      displayMode,
    };
  },
);

const FLIP_TRANSFORM_VALUES = {
  vertical: 'scale(1, -1)',
  horizontal: 'scale(-1, 1)',
  both: 'scale(-1, -1)',
  none: null,
} as const;

const FLIP_TRANSFORM_STYLES = {
  x: FLIP_TRANSFORM_VALUES.vertical,
  y: FLIP_TRANSFORM_VALUES.horizontal,
  xy: FLIP_TRANSFORM_VALUES.both,
  none: FLIP_TRANSFORM_VALUES.none,
  vertical: FLIP_TRANSFORM_VALUES.vertical,
  horizontal: FLIP_TRANSFORM_VALUES.horizontal,
  both: FLIP_TRANSFORM_VALUES.both,
} as const;

function getTransformFlipVar(flip: keyof typeof FLIP_TRANSFORM_STYLES) {
  const value = FLIP_TRANSFORM_STYLES[flip];
  if (!value) {
    return {};
  }
  return {
    '--flip': value,
  };
}
type SvgImageCss = {
  '--flip'?: (typeof FLIP_TRANSFORM_STYLES)[keyof typeof FLIP_TRANSFORM_STYLES];
};

export const css = withCompInfo<SvgImageCss, SvgImageDefinition>()(
  ['styleProperties'],
  ({ styleProperties }) => {
    const flip = styleProperties.flip as 'x' | 'y' | 'xy' | 'none';

    return {
      ...getTransformFlipVar(flip),
    };
  },
);

export const sdkData = withCompInfo<
  any,
  SvgImageDefinition,
  SvgImageCarmiProps
>()(
  ['compId', 'compData', 'topology'],
  ({ compData: { svgId, alt, displayMode }, compId, topology }) => {
    const { mediaRootUrl } = topology;
    const { getSvgBaseUrl } = vectorImage.buildSvgUrlFactory();
    const mediaSvgUrl = getSvgBaseUrl(mediaRootUrl);

    return {
      id: compId,
      mediaSvgUrl,
      svgId,
      alt,
      displayMode,
    };
  },
);

export default createComponentMapperModel({
  props,
  sdkData,
  css,
});
